/**
 * ChampsModele — un champ de saisie par variable manquante du modèle.
 *
 * ─── LE PROBLÈME QU'IL RÈGLE ────────────────────────────────────────────────
 *
 * Choisir un modèle rendait un texte truffé de `{{accolades}}` que le rédacteur
 * devait traquer à la main dans trois pages. Sur l'arrêté de circulation, cela
 * faisait dix variables à retrouver : demandeur, date de la demande, nature des
 * travaux, localisation, dates, modalités, longueur, itinéraire de déviation,
 * responsable de la signalisation.
 *
 * ─── CE QU'IL FAIT ──────────────────────────────────────────────────────────
 *
 * Un champ par trou, avec la bonne saisie : un calendrier pour une date, un
 * nombre pour une longueur, la liste des agents pour un agent, celle des lieux
 * pour une localisation.
 *
 * ─── LES LISTES RESTENT OUVERTES ────────────────────────────────────────────
 *
 * Une liste déroulante propose l'annuaire, mais n'y enferme pas : un arrêté peut
 * viser une rue qui n'est pas au référentiel des lieux, ou une entreprise qui
 * n'est pas encore à l'annuaire des tiers. On propose, on n'impose pas — sinon
 * l'agent est bloqué devant un acte qu'il doit prendre aujourd'hui.
 */

import { useEffect, useState } from 'react';
import { Field, Input } from '@/components/ui';

/** Même gabarit que les autres menus de l'application. */
const CLASSE_SELECT =
  'h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech';
import { api } from '@/lib/api';

export interface ChampModele {
  cle: string;
  libelle: string;
  type: 'texte' | 'texte_long' | 'date' | 'nombre' | 'liste' | 'liste_fermee' | 'oui_non';
  source?: 'agents' | 'lieux' | 'adresses' | 'tiers' | 'associations'
    | 'grades' | 'cadres_emploi' | 'echelons' | 'categories' | 'services';
  aide?: string;
}

/** Les annuaires, chargés une fois et seulement si un champ en a besoin. */
type Annuaires = Partial<Record<NonNullable<ChampModele['source']>, string[]>>;

/**
 * Les listes FERMÉES ne se saisissent pas.
 *
 * Demande d'Henrico (28/07) : « il faut que ce soit une liste déroulante qui
 * aille du plus petit au plus grand pour qu'il n'y ait pas d'erreur possible ».
 *
 * La distinction avec les listes ouvertes n'est pas cosmétique. Un arrêté peut
 * viser une rue absente du référentiel : on propose sans enfermer. Mais un
 * GRADE hors nomenclature n'existe pas statutairement, et laisser saisir
 * produit « Adjoint technique 2e cl. » à côté de « Adjoint technique 2ème
 * classe » — deux lignes pour un même grade, et un catalogue inexploitable dès
 * qu'on cherche.
 */
const SOURCES_FERMEES: ReadonlySet<string> = new Set([
  'grades', 'cadres_emploi', 'echelons', 'categories', 'services',
]);

export function ChampsModele({
  champs,
  valeurs,
  onChange,
}: {
  champs: ChampModele[];
  valeurs: Record<string, string>;
  onChange: (cle: string, valeur: string) => void;
}) {
  const [annuaires, setAnnuaires] = useState<Annuaires>({});

  useEffect(() => {
    const sources = [...new Set(champs.map((c) => c.source).filter(Boolean))] as NonNullable<
      ChampModele['source']
    >[];
    if (sources.length === 0) return;

    void (async () => {
      const resultat: Annuaires = {};
      await Promise.all(
        sources.map(async (src) => {
          try {
            if (src === 'agents') {
              const r = await api.serviceTechnique.signalements.agents();
              resultat.agents = r.items.map((a) => a.fullName).filter(Boolean).sort();
            } else if (src === 'lieux') {
              const r = await api.admin.lieux.list();
              resultat.lieux = r.lieux.map((l) => l.nom).filter(Boolean).sort();
            } else if (src === 'adresses') {
              // ⚠️ L'ADRESSE, pas le NOM. `{{adresse_fourriere}}` attend une
              // adresse postale : y injecter « SALLE DES FÊTES » ne veut rien
              // dire sur un arrêté. Un lieu sans adresse renseignée est écarté
              // plutôt que proposé vide.
              const r = await api.admin.lieux.list();
              resultat.adresses = r.lieux
                .map((l) => [l.adresse, l.codePostal, l.ville]
                  .filter(Boolean).join(' ').trim())
                .filter((a) => a.length > 0)
                .sort();
            } else if (src === 'tiers') {
              const r = await api.notesFrais.tiers.list();
              resultat.tiers = r.map((t) => t.nom).filter(Boolean).sort();
            } else if (src === 'grades' || src === 'cadres_emploi'
                       || src === 'echelons' || src === 'services') {
              const r = await api.rh.referentielsPourActes();
              resultat.grades = r.grades.map((g) => g.libelle).sort();
              resultat.cadres_emploi = r.cadresEmploi.map((c) => c.libelle).sort();
              resultat.services = r.services
                .map((x) => x.libelleOfficiel ?? x.libelle).sort();
              // Les échelons vont du plus petit au plus grand — c'est la
              // demande exacte. On prend le plus grand `echelonsMax` connu :
              // le grade n'est pas encore choisi quand la liste se construit,
              // et borner trop court empêcherait de saisir un échelon valide.
              const maxi = Math.max(
                1, ...r.grades.map((g) => g.echelonsMax ?? 0));
              resultat.echelons = Array.from({ length: maxi }, (_, i) => String(i + 1));
            } else if (src === 'categories') {
              // Fermée et non paramétrable : elle relève du statut.
              resultat.categories = ['A', 'B', 'C'];
            }
            // `associations` : pas d'annuaire simple exposé aujourd'hui — le
            // champ reste en saisie libre plutôt que d'afficher une liste vide.
          } catch {
            // Un annuaire indisponible ne doit pas bloquer la rédaction : le
            // champ retombe en saisie libre.
          }
        })
      );
      setAnnuaires(resultat);
    })();
  }, [champs]);

  if (champs.length === 0) return null;

  return (
    <div className="space-y-3 rounded-r-lg border border-border bg-surface/40 p-3">
      <div>
        <h3 className="text-sm font-medium text-ink">
          {champs.length} champ{champs.length > 1 ? 's' : ''} à compléter
        </h3>
        <p className="mt-0.5 text-xs text-ink-subtle">
          Ce qui reste après ce que la commune et la fiche d'origine ont déjà rempli.
        </p>
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        {champs.map((c) => {
          const valeur = valeurs[c.cle] ?? '';
          const options = c.source ? annuaires[c.source] : undefined;
          const idChamp = `champ-${c.cle}`;
          const large = c.type === 'texte_long';

          return (
            <div key={c.cle} className={large ? 'sm:col-span-2' : undefined}>
              <Field label={c.libelle} htmlFor={idChamp} hint={c.aide}>
                {large ? (
                  <textarea
                    id={idChamp}
                    value={valeur}
                    onChange={(e) => onChange(c.cle, e.target.value)}
                    rows={3}
                    className="w-full rounded-r-lg border border-border bg-surface/60 p-2.5 text-sm text-ink focus:border-tech focus:outline-none focus:ring-2 focus:ring-tech focus:ring-offset-2 focus:ring-offset-bg"
                  />
                ) : c.type === 'oui_non' ? (
                  /* Une question fermée mérite deux choix, pas un champ libre
                     où l'on écrira « oui », « OUI », « o » ou « néant ». */
                  <select
                    id={idChamp}
                    value={valeur}
                    onChange={(e) => onChange(c.cle, e.target.value)}
                    className={CLASSE_SELECT}
                  >
                    <option value="">— Choisir —</option>
                    <option value="oui">Oui</option>
                    <option value="non">Non</option>
                  </select>
                ) : c.type === 'liste_fermee' && options && options.length > 0 ? (
                  /* Liste FERMÉE : le référentiel fait foi. Pas de saisie
                     libre — c'est ce qui garantit qu'un grade s'écrit d'une
                     seule façon dans toute la commune. */
                  <select
                    id={idChamp}
                    value={valeur}
                    onChange={(e) => onChange(c.cle, e.target.value)}
                    className={CLASSE_SELECT}
                  >
                    <option value="">— Choisir —</option>
                    {options.map((o) => (
                      <option key={o} value={o}>{o}</option>
                    ))}
                  </select>
                ) : c.type === 'liste_fermee' ? (
                  /* Référentiel VIDE : on ne bloque pas le rédacteur devant un
                     menu sans option — il a un acte à prendre aujourd'hui. On
                     retombe en saisie libre, et on dit pourquoi. */
                  <>
                    <Input
                      id={idChamp}
                      value={valeur}
                      onChange={(e) => onChange(c.cle, e.target.value)}
                    />
                    <p className="mt-1 text-2xs text-ink-subtle">
                      Aucune valeur au référentiel — saisie libre en attendant.
                      Renseignez-le dans Ressources humaines → Référentiels.
                    </p>
                  </>
                ) : options && options.length > 0 ? (
                  <>
                    {/* Liste ouverte : on propose l'annuaire sans y enfermer. */}
                    <Input
                      id={idChamp}
                      list={`${idChamp}-liste`}
                      value={valeur}
                      onChange={(e) => onChange(c.cle, e.target.value)}
                      placeholder="Choisir ou saisir…"
                    />
                    <datalist id={`${idChamp}-liste`}>
                      {options.map((o) => (
                        <option key={o} value={o} />
                      ))}
                    </datalist>
                  </>
                ) : (
                  <Input
                    id={idChamp}
                    type={c.type === 'date' ? 'date' : c.type === 'nombre' ? 'number' : 'text'}
                    value={valeur}
                    onChange={(e) => onChange(c.cle, e.target.value)}
                  />
                )}
              </Field>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/**
 * Remplace les `{{cles}}` par les valeurs saisies.
 *
 * ⚠️ Une valeur laissée vide NE remplace PAS : la variable garde ses accolades.
 * Un acte où « sur une longueur de  mètres » aurait remplacé
 * « sur une longueur de {{longueur}} mètres » partirait à la signature sans que
 * personne ne voie le trou.
 */
export function appliquerChamps(corps: string, valeurs: Record<string, string>): string {
  return corps.replace(/\{\{([a-z0-9_]+)\}\}/gi, (accolades, cle: string) => {
    const v = valeurs[cle.toLowerCase()];
    return v && v.trim() ? v.trim() : accolades;
  });
}
